import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { FooterComponent } from '../../domains/navigation/footer/footer.component';
import { CtBatchStoreService } from '../../service/app-state/ct-batch-store.service';
import {
  catchError,
  debounceTime,
  defaultIfEmpty,
  EMPTY,
  filter,
  from,
  last,
  map,
  Observable,
  switchMap,
  tap,
} from 'rxjs';
import { BuildSimpleExtractedDataProcessingService } from '../../build-simple/build-simple-extracted-data-processing.service';
import { WorkflowService } from '../../service/app-state/workflow.service';
import { OcrService } from '../../service/ocr/ocr.service';
import {
  CtBatch,
  CtBatchApiService,
  CtHeaderComponent,
  eingangIdOf,
  PageImageService,
  SharedCtBatchStoreService,
  ClaimsToastService,
} from '@ps-gui/claims-terminal-shared';
import { Ct2ConfigService } from '../../service/ct2-config.service';
import { CtBatchExtractionApiService } from '../../service/api/ct-batch-extraction-api.service';
import {
  AuthenticationService,
  EingangEventsService,
  LoadingPageComponent,
} from '@ps-gui/shared-core';
import { BatchLockGuardService } from '../../service/batch-lock-guard.service';
import { BuildSimpleExtractionResultDto } from '../../models/build-simple-model';
import { AppViewPageComponent } from '../app-view-page/app-view-page.component';
import {
  DefaultInputStylingService,
  INPUT_STYLING_SERVICE,
} from '../../service/input-styling.service';
import { onErrorThrow, squeezeMap } from '../../utils/Rxjs.utils';

const LOCK_CHECK_DEBOUNCE_MS = 500;

@Component({
  selector: 'ct-extraction-batch',
  providers: [
    {
      provide: INPUT_STYLING_SERVICE,
      useClass: DefaultInputStylingService,
    },
  ],
  imports: [FooterComponent, LoadingPageComponent, AppViewPageComponent, CtHeaderComponent],
  templateUrl: './batch.component.html',
})
export class BatchComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly toastService = inject(ClaimsToastService);
  private readonly ctBatchApiService = inject(CtBatchApiService);
  private readonly ctBatchService = inject(CtBatchApiService);
  private readonly ctBatchExtractionApiService = inject(CtBatchExtractionApiService);
  private readonly buildSimpleExtractedDataProcessingService = inject(
    BuildSimpleExtractedDataProcessingService,
  );
  private readonly pageImageService = inject(PageImageService);
  private readonly ocrService = inject(OcrService);
  private readonly configService = inject(Ct2ConfigService);
  private readonly eingangEventsService = inject(EingangEventsService);
  private readonly batchLockGuard = inject(BatchLockGuardService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly workflowService = inject(WorkflowService);

  protected readonly ctBatchStoreService = inject(CtBatchStoreService);
  protected readonly sharedCtBatchStoreService = inject(SharedCtBatchStoreService);

  protected userId = '<loading>';
  protected userFullName = '';
  protected doneLoading = false;

  // TODO: extract this in some monitoring service
  protected completeOcrRequestsCount = 0;

  ctBatch: CtBatch = {} as CtBatch;

  ngOnInit(): void {
    from(this.authenticationService.loadUserProfile())
      .pipe(
        map((userProfile) => {
          this.userId = userProfile.id ?? '<unknown user id>';
          this.userFullName = userProfile.username ?? '<unknown user>';
        }),
        switchMap(() => this.route.paramMap),
        map((params) => ({ retrievedCtBatchId: this.getRouteBatchIdParameter(params) })),
        switchMap(async ({ retrievedCtBatchId }) => {
          const oldId = this.ctBatchStoreService.ctBatchId;
          await this.ctBatchStoreService.setCtBatchId(retrievedCtBatchId);
          return { oldId, retrievedCtBatchId };
        }),
        switchMap(({ oldId, retrievedCtBatchId }) =>
          this.loadCtBatch(
            retrievedCtBatchId,
            oldId !== retrievedCtBatchId &&
              !this.configService.config.ignoreBuildSimpleExtractionResults,
          ),
        ),
        map((result) => {
          const sortedDocumentList = result.batch.documents.toSorted(
            (document1, document2) => document1.index - document2.index,
          );
          const batch = { ...result.batch };
          batch.documents = sortedDocumentList;
          this.ctBatchStoreService.ctBatch = batch;
          this.sharedCtBatchStoreService.ctBatch = batch;
          return batch;
        }),
        switchMap(() => this.lockCtBatch()),
        switchMap(() => this.workflowService.init()),
        tap(() => {
          this.doneLoading = true;
          this.startLockSupervision();
        }),
      )
      .subscribe({
        error: (error: any) => {
          console.error(error);
          this.toastService.error(`Vorgang konnte nicht geladen werden`);
        },
      });
  }

  private getRouteBatchIdParameter(params: ParamMap) {
    const ctBatchId = params.get('id');

    if (!ctBatchId) {
      throw new Error('Invalid path - expected /batch/{batch-id}');
    }

    return ctBatchId;
  }

  loadCtBatch(ctBatchId: string, shouldUtilizeExtractionData: boolean) {
    return this.ctBatchService.getCtBatch(ctBatchId).pipe(
      squeezeMap((ctBatch) =>
        this.ocrService.init(ctBatch).pipe(
          tap((finishedCount) => (this.completeOcrRequestsCount = finishedCount)),
          defaultIfEmpty(0),
          last(),
        ),
      ),
      switchMap((batch) =>
        this.ctBatchExtractionApiService
          .loadExtractionDataByDocumentId(batch)
          .pipe(map((extractionDataByDocumentId) => ({ batch, extractionDataByDocumentId }))),
      ),
      squeezeMap(({ batch, extractionDataByDocumentId }) => {
        if (shouldUtilizeExtractionData) {
          const flattenedExtractionDataByDocumentId = Object.keys(
            extractionDataByDocumentId,
          ).reduce(
            (acc, documentId) => {
              const extractionData = extractionDataByDocumentId[documentId];
              acc[documentId] = Object.values(extractionData)[0] as
                | BuildSimpleExtractionResultDto
                | undefined;
              return acc;
            },
            {} as Record<string, BuildSimpleExtractionResultDto | undefined>,
          );
          return this.buildSimpleExtractedDataProcessingService.storeExtractedSuggestions(
            batch,
            flattenedExtractionDataByDocumentId,
          );
        }
        return Promise.resolve();
      }),
    );
  }

  lockCtBatch(): Observable<void> {
    return from(this.authenticationService.getUserName()).pipe(
      onErrorThrow('Username not present'),
      switchMap(() => this.ctBatchApiService.lockCtBatch(this.ctBatchStoreService.ctBatch.id)),
      onErrorThrow(`Fehler beim Sperren des Vorgangs`),
    );
  }

  /** Notice when this batch gets taken away from us while we are working on it */
  private startLockSupervision(): void {
    const batch = this.ctBatchStoreService.ctBatch;
    const eingangId = eingangIdOf(batch);

    if (!eingangId) {
      return;
    }

    this.eingangEventsService.eingangChanged$
      .pipe(
        filter((event) => event.eingangId === eingangId),
        // A takeover arrives as remove-then-set, so one takeover emits two events
        debounceTime(LOCK_CHECK_DEBOUNCE_MS),
        switchMap(() =>
          this.batchLockGuard.assureStillAssignedToCurrentUser(batch).pipe(catchError(() => EMPTY)),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
